import React, { Component, FormEvent, MouseEvent } from 'react';
import { connect } from 'react-redux';
import { filter, find, map, sortBy, values } from 'lodash';
import { FormItem, Input } from 'elmo-elements';
import {
  SelectPropsOption,
  SelectPropsOptionGrouped,
} from 'elmo-elements/Select';
import {
  ErrorBox,
  PageDialog,
  PageDialogCancel,
  PageDialogSubmit,
} from 'element';
import { OnChangeSelectEvent } from 'element/Select/types';
import {
  AccountTreeArea,
  AccountTreeRole,
  AccountTreeSelector,
  AccountTreeSite,
  LanguagePreferences,
  StringMap,
  UserRole,
} from 'type';
import { capitalize, getFullUserRole, keyMirror } from 'lib/helpers';
import { StoreState } from 'state/types';
import { asSelectOptionsCombiner } from 'state/combiners';
import { getLangPreferences } from 'state/Account';
import {
  activeAreasBySiteIdCombiner,
  areasGroupedAsSelectOptionsCombiner,
} from 'state/AccountTree/combiners';
import { getAccountTree } from 'state/AccountTree';
import { currentUserSiteIdsSelector, hasPermissionSelector } from 'state/Auth';
import { SubmitPayloadWithPayRate } from '../../types';
import './RoleModalWithPayRates.scss';
import { RoleModalViewProps } from './types';
import {
  DialogContent,
  ListSubheader,
  MenuItem,
  Select,
} from 'extended-oxygen-elements';
import { SelectChangeEvent } from '@mui/material';
import { DialogActions, DialogTitle } from 'extended-oxygen-elements';
import { NewPayRatesAlert } from '../../../../page/User/page/Profile/components/Role/components/NewPayRatesAlert';
import { Radio, NativeTable, Paragraph, Text } from 'elmo-elements';

type UserRoleSimplified = Pick<
  UserRole,
  'site_id' | 'area_id' | 'role_id' | 'is_main'
> & {
  pay_rate?: number | null;
};

type OwnProps = RoleModalViewProps;

type StateProps = {
  langPreferences: LanguagePreferences;
  tree: AccountTreeSelector;
  currentUserSiteIds: string[];
  canViewAllSites: boolean;
  hasPayRateEditPermission: boolean;
};

type DispatchProps = {};

type Props = OwnProps & StateProps & DispatchProps;

type FormData = {
  role_id: string | null;
  user_roles: UserRoleSimplified[];
};

type State = {
  data: FormData;
  isValid: boolean;
  isEditMainRoleEnabled: boolean;
};

const getState = ({ initRoles = [] }: Props): State => ({
  data: {
    role_id: null,
    user_roles: initRoles.map((role) => ({
      site_id: role.site_id,
      area_id: role.area_id,
      role_id: role.role_id,
      is_main: role.is_main,
      pay_rate: role.pay_rate ?? null,
    })),
  },
  isValid: false,
  isEditMainRoleEnabled: false,
});

export class RoleModalWithPayRatesComponent extends Component<Props, State> {
  state = getState(this.props);
  names = keyMirror(this.state.data);

  render() {
    const {
      state: { data, isEditMainRoleEnabled },
      names,
      labels,
    } = this;
    const { hasPayRateEditPermission } = this.props;

    return (
      <PageDialog
        maxWidth={'md'}
        open={this.props.isOpened}
        id="user-add-roles-modal-with-pay-rates"
        onClose={this.onCloseModal}
      >
        <DialogTitle>Role Assignment</DialogTitle>
        <DialogContent>
          <form onSubmit={this.onSubmit}>
            <ErrorBox errors={this.props.errors} className="mb-6" />

            <FormItem label={`Add ${labels.role} *`}>
              <Select
                fullWidth
                disabled={isEditMainRoleEnabled}
                id="role"
                name={names.role_id}
                onChange={this.onSelectChanged}
                value={data.role_id || ''}
                aria-label={`Add ${labels.role}`}
              >
                {this.roleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormItem>

            <FormItem label={`${capitalize(labels.area)} / ${capitalize(labels.site)} *`}>
              <Select
                disabled={isEditMainRoleEnabled || !data.role_id}
                fullWidth
                id="area"
                name={'areaId'}
                aria-label={capitalize(`${labels.area}, ${labels.site}`)}
                onChange={this.handleChangeArea}
                value={''}
              >
                {this.areaOptions.map((option) =>
                  this.renderSelectGroup(option)
                )}
              </Select>
            </FormItem>

            <NewPayRatesAlert />

            <div className="role-modal-with-pay-rates__roles-table">
              <NativeTable isFullWidth={true}>
                <NativeTable.Header>
                  <NativeTable.Tr>
                    <NativeTable.Th width={'56px'}>
                      <Text size={'sm'} isBold>Primary</Text>
                    </NativeTable.Th>
                    <NativeTable.Th>
                      <Text size={'sm'} isBold>Role</Text>
                    </NativeTable.Th>
                    <NativeTable.Th width={'100px'}>
                      <Text size={'sm'} isBold>Department</Text>
                    </NativeTable.Th>
                    <NativeTable.Th width={'100px'}>
                      <Text size={'sm'} isBold>Location</Text>
                    </NativeTable.Th>
                    {hasPayRateEditPermission && (
                      <NativeTable.Th width={'120px'}>
                        <Text size={'sm'} isBold>Pay Rate</Text>
                      </NativeTable.Th>
                    )}
                  </NativeTable.Tr>
                </NativeTable.Header>
                <NativeTable.Body>
                  {map(data.user_roles, (userRole, index) =>
                    this.renderUserRoleRow(userRole, index)
                  )}
                </NativeTable.Body>
              </NativeTable>
            </div>
          </form>
        </DialogContent>
        <DialogActions>
          <PageDialogCancel onClick={this.onCloseModal} fullWidth={false}>
            Cancel
          </PageDialogCancel>
          <PageDialogSubmit
            onClick={this.onSubmit}
            loading={this.props.isUpdating}
            disabled={this.isSaveBtnDisabled}
            fullWidth={false}
          >
            Save
          </PageDialogSubmit>
        </DialogActions>
      </PageDialog>
    );
  }

  handleChangeArea = (e: SelectChangeEvent<string>, child: React.ReactNode) => {
    const { value, name } = e.target;
    this.onSelectChangeArea({ value, name });
  };

  renderSelectGroup = (group: SelectPropsOptionGrouped) => {
    const items = group.options.map(
      (option: SelectPropsOption, index: number) => {
        return (
          <MenuItem key={`${option.label}-${index}`} value={option.value}>
            {option.label}
          </MenuItem>
        );
      }
    );
    return [<ListSubheader>{group.label}</ListSubheader>, items];
  };

  renderUserRoleRow = (userRole: UserRoleSimplified, index: number) => {
    const { tree, hasPayRateEditPermission } = this.props;
    const { role_id, area_id, site_id, is_main, pay_rate } = userRole;
    const role = tree.roles[role_id];
    const area = tree.areas[area_id];
    const site = tree.sites[site_id];
    const isArchived = role && role.archived;

    if (isArchived) {
      return null;
    }

    return (
      <NativeTable.Tr key={`${role_id}-${area_id}-${site_id}-${index}`}>
        <NativeTable.Td>
          <Radio
            name="primary-role"
            value={this.getRoleValue(userRole)}
            checked={is_main}
            onChange={() => this.handlePrimaryRoleChangeByValue(this.getRoleValue(userRole))}
            disabled={this.getIsDisabledRoleState(userRole)}
          />
        </NativeTable.Td>
        <NativeTable.Td>
          <Paragraph size={'sm'}>
            {getFullUserRole({
              ...tree,
              role: {
                ...userRole,
                site_name: site?.name || '',
                area_name: area?.name || '',
                role_name: role?.name || '',
              },
              showPrimaryLabel: false,
            })}
          </Paragraph>
        </NativeTable.Td>
        <NativeTable.Td>
          <Paragraph size={'sm'}>
            {area?.name || '-'}
          </Paragraph>
        </NativeTable.Td>
        <NativeTable.Td>
          <Paragraph size={'sm'}>
            {site?.name || '-'}
          </Paragraph>
        </NativeTable.Td>
        {hasPayRateEditPermission && (
          <NativeTable.Td>
            <Input
              name={`pay_rate_${role_id}_${area_id}_${site_id}`}
              value={pay_rate?.toString() || ''}
              htmlType={'number'}
              onChange={(e) =>
                this.handlePayRateChange(userRole, e.target.value)
              }
              placeholder="..."
            />
          </NativeTable.Td>
        )}
      </NativeTable.Tr>
    );
  };

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>,
    snapshot?: any
  ): void {
    if (prevProps.isOpened !== this.props.isOpened) {
      if (this.props.isOpened) {
        // onOpen
        this.setState(getState(this.props));
      } else {
        // onClose
        this.setState({
          isValid: false,
        });
      }
    }
  }

  private get isSaveBtnDisabled(): boolean {
    const { isValid, isEditMainRoleEnabled } = this.state;

    return isEditMainRoleEnabled || !isValid;
  }

  private get labels() {
    const {
      langPreferences: { role, area, site },
    } = this.props;

    return {
      role: role.singular,
      area: area.singular,
      site: site.singular,
    };
  }

  private get roleOptions(): SelectPropsOption[] {
    let roles: StringMap<AccountTreeRole> = {};
    Object.keys(this.props.tree.roles).forEach((key) => {
      if (!this.props.tree.roles[key].archived) {
        roles[key] = this.props.tree.roles[key];
      }
    });
    return sortBy(values(asSelectOptionsCombiner(roles)), 'label');
  }

  private get areaOptions(): SelectPropsOptionGrouped[] {
    const { role_id } = this.state.data;

    if (role_id === null) {
      return [];
    }

    const { areas, sites } = this.props.tree;

    const filteredAreas: AccountTreeArea[] = filter(
      areas,
      (area: AccountTreeArea) =>
        area.role_ids.includes(role_id) && !area.archived
    );

    const areasBySiteId = activeAreasBySiteIdCombiner(filteredAreas);
    const filteredSites = filter(sites, (site) => !site.archived);
    const areasByActiveSiteId: StringMap<AccountTreeArea[]> = {};
    filteredSites.forEach((site) => {
      if (areasBySiteId[site.id]) {
        areasByActiveSiteId[site.id] = areasBySiteId[site.id];
      }
    });

    return areasGroupedAsSelectOptionsCombiner(
      areasByActiveSiteId,
      getActiveSites(sites)
    );
  }

  private get submitPayload(): SubmitPayloadWithPayRate {
    const { user_roles } = this.state.data;

    return {
      user_roles: user_roles.map((role) => ({
        site_id: role.site_id,
        area_id: role.area_id,
        role_id: role.role_id,
        pay_rate: role.pay_rate ?? null,
      })),
    };
  }

  private getRoleValue = (role: UserRoleSimplified): string => {
    return `${role.role_id}-${role.area_id}-${role.site_id}`;
  };

  private getPrimaryRoleValue = (): string | number => {
    const primaryRole = find(
      this.state.data.user_roles,
      (role) => role.is_main
    );
    return primaryRole ? this.getRoleValue(primaryRole) : '';
  };

  private handlePrimaryRoleChangeByValue = (value: string | number) => {
    const { user_roles } = this.state.data;
    const selectedRole = find(
      user_roles,
      (role) => this.getRoleValue(role) === value
    );

    if (selectedRole) {
      const updatedRoles = map(user_roles, (role) => ({
        ...role,
        is_main: isEqualRoles(role, selectedRole),
      }));

      this.updateRoles(updatedRoles);
    }
  };

  private handlePayRateChange = (
    role: UserRoleSimplified,
    value: string
  ) => {
    const { user_roles } = this.state.data;
    const numericValue = value === '' ? null : parseFloat(value);

    const updatedRoles = map(user_roles, (r) =>
      isEqualRoles(r, role)
        ? { ...r, pay_rate: isNaN(numericValue as number) ? null : numericValue }
        : r
    );

    this.updateRoles(updatedRoles);
  };

  private getIsDisabledRoleState = ({ is_main, site_id }: UserRoleSimplified) => {
    const { isEditMainRoleEnabled } = this.state;

    if (isEditMainRoleEnabled) {
      return false;
    }

    const { canViewAllSites, currentUserSiteIds } = this.props;

    const hasAccessToSiteOfRole: boolean =
      canViewAllSites || currentUserSiteIds.includes(site_id);

    return is_main || !hasAccessToSiteOfRole;
  };

  private onSelectChanged = (
    e: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const { name, value } = e.target;
    this.setState({
      data: {
        ...this.state.data,
        [name]: value,
      },
    });
  };

  private onSelectChangeArea = ({ value: areaId }: OnChangeSelectEvent) => {
    const area = this.props.tree.areas[areaId];
    if (!area) {
      console.warn(`Area ${areaId} not found in tree`);
      return;
    }

    const siteId = area.site_id;

    const partialNewRole: UserRoleSimplified = {
      role_id: this.state.data.role_id!,
      area_id: areaId,
      site_id: siteId,
      is_main: false,
      pay_rate: null,
    };

    const userHadNewRole = !!find(
      this.state.data.user_roles,
      (existingRole: UserRoleSimplified) =>
        existingRole.site_id === partialNewRole.site_id &&
        existingRole.area_id === partialNewRole.area_id &&
        existingRole.role_id === partialNewRole.role_id
    );

    if (!userHadNewRole) {
      this.setState(
        {
          data: {
            role_id: null,
            user_roles: [...this.state.data.user_roles, partialNewRole],
          },
        },
        this.validate
      );
    } else {
      this.setState({
        data: {
          ...this.state.data,
          role_id: null,
        },
      });
    }
  };

  private onSubmit = (
    event: MouseEvent<HTMLButtonElement> | FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (this.state.isValid) {
      this.props.onFormSubmit(this.submitPayload as any);
    }
  };

  private validate = () => {
    this.setState({
      isValid: !!this.state.data.user_roles.length,
    });
  };

  private updateRoles = (updatedRoles: UserRoleSimplified[]) => {
    this.setState(
      (prevState) => ({
        data: {
          ...prevState.data,
          user_roles: updatedRoles,
        },
      }),
      this.validate
    );
  };

  private onCloseModal = () => {
    if (!this.props.isUpdating) {
      this.props.onCloseModal();
    }
  };
}

const mapStateToProps = (state: StoreState): StateProps => ({
  langPreferences: getLangPreferences(state),
  tree: getAccountTree(state),
  currentUserSiteIds: currentUserSiteIdsSelector(state),
  canViewAllSites: hasPermissionSelector(state, 'account.viewallsites'),
  hasPayRateEditPermission: hasPermissionSelector(
    state,
    'payrate.edit'
  ),
});

export const RoleModalWithPayRates = connect(mapStateToProps)(
  RoleModalWithPayRatesComponent
);

function isEqualRoles(
  role: Pick<UserRoleSimplified, 'site_id' | 'area_id' | 'role_id'>,
  anotherRole: Pick<UserRoleSimplified, 'site_id' | 'area_id' | 'role_id'>
): boolean {
  return (
    role.site_id === anotherRole.site_id &&
    role.area_id === anotherRole.area_id &&
    role.role_id === anotherRole.role_id
  );
}

export function getActiveSites(sites: StringMap<AccountTreeSite>) {
  const filteredSites = filter(
    sites,
    (site: AccountTreeSite) => !site.archived
  );
  const activeSites: StringMap<AccountTreeSite> = {};

  filteredSites.forEach((site) => {
    activeSites[site.id] = site;
  });

  return activeSites;
}

